/**
 * Fonte única de verdade para os dados estruturados (JSON-LD) do site.
 *
 * Por que centralizar:
 * - Evita variações inválidas espalhadas por componentes (logos 404, campos
 *   obrigatórios ausentes, propriedades vazias).
 * - Garante que `Organization` e `WebSite` existam UMA única vez por documento
 *   (elas são declaradas estaticamente no index.html e apenas referenciadas
 *   por `@id` nas demais entidades).
 */

export const SITE_URL = 'https://ameup.com.br';

export const ORGANIZATION_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

/** Logo real e acessível (695x149). Não usar /logo.png — retorna 404. */
export const ORGANIZATION_LOGO = {
  '@type': 'ImageObject',
  url: `${SITE_URL}/ameup-logo.png`,
  width: 695,
  height: 149,
} as const;

export const DEFAULT_SOCIAL_IMAGE =
  'https://storage.googleapis.com/gpt-engineer-file-uploads/boSWd1Vp6rRWei5Ijhs7Ejl8NQ02/social-images/social-1758936599280-67238655.png';

/** Referência leve à Organization declarada no index.html. */
export const organizationRef = { '@id': ORGANIZATION_ID } as const;

type JsonValue = unknown;

/**
 * Remove recursivamente `undefined`, `null`, strings vazias, arrays vazios e
 * objetos vazios. O Rich Results Test acusa erro em propriedades vazias, então
 * é preferível omitir a propriedade a emiti-la sem valor.
 */
export function pruneEmpty<T extends JsonValue>(value: T): T | undefined {
  if (Array.isArray(value)) {
    const cleaned = value
      .map((item) => pruneEmpty(item))
      .filter((item) => item !== undefined);
    return (cleaned.length > 0 ? cleaned : undefined) as T | undefined;
  }

  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => [key, pruneEmpty(item)] as const)
      .filter(([, item]) => item !== undefined);
    return (entries.length > 0
      ? (Object.fromEntries(entries) as T)
      : undefined) as T | undefined;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    return (trimmed.length > 0 ? (trimmed as unknown as T) : undefined);
  }

  return value === undefined || value === null ? undefined : value;
}

/** Serializa um schema já limpo; retorna `null` quando nada sobrou. */
export function serializeSchema(schema: unknown): string | null {
  const cleaned = pruneEmpty(schema);
  return cleaned ? JSON.stringify(cleaned) : null;
}

/** Headline do schema Article/BlogPosting: Google limita a 110 caracteres. */
export function toHeadline(title: string): string {
  const clean = title.replace(/\s*\|\s*AmeUp\s*$/i, '').trim();
  return clean.length > 110 ? `${clean.slice(0, 107).trimEnd()}...` : clean;
}

/** Datas ISO-8601 válidas — schemas com data inválida são rejeitados. */
export function toIsoDate(value?: string | null): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}
